import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CardHolderNameService } from "../../services/card-holder-name.service";
import { SavedCardDto } from "../saved-card-list/models/saved-card.models";
import { CardDataService } from "../saved-card-list/services/card-data.service";
import { PaymentToCardDataService } from "./services/payment-to-card-data.service";
import { CommissionsSizeDto, P2PRequestDto } from "./models/payment-to-card.models";
import { debounceTime, distinctUntilChanged, filter, map } from "rxjs";
import { NotificationService } from "../../services/notification.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { PermissionService } from "src/app/services/permission.service";

@Component({
	selector: "app-to-card-list",
	templateUrl: "./payment-to-card.component.html",
	styleUrls: ["./payment-to-card.component.scss"],
})
export class PaymentToCardComponent implements OnInit {
	tabSelect: string | "new" | "saved" = "new";

	currency: string | null = null;

	savedCardList: SavedCardDto[] = [];
	selectedCard: SavedCardDto | undefined;

	form!: FormGroup;

	cardNumberFrom = "";
	mount = "";
	year = "";
	cvv = "";
	cardHolderNameFrom = "";

	p2pRefund = false;
	presetSum = false;
	token = "";

	virtualPosId = "";

	commission = "0";

	loaded = false;

	isSending = false;

	time = 0;
	timer: any;

	constructor(
		private activatedRoute: ActivatedRoute,
		private cardHolderNameService: CardHolderNameService,
		private cardDataService: CardDataService,
		private paymentToCardDataService: PaymentToCardDataService,
		private notificationService: NotificationService,
		private router: Router,
		private permissionService: PermissionService
	) {}

	ngOnInit() {
		this.getVirtualPosId();

		if (this.permissionService.hasPermission(["CARDS_VIEW"])) {
			this.loadCardList();
		}
	}

	initForm(): void {
		this.form = new FormGroup({
			cardNumberFormControl: new FormControl("", [Validators.minLength(16), Validators.required]),
			sumFormControl: new FormControl("", [Validators.minLength(16), Validators.required]),
			currencyFormControl: new FormControl("KZT"),
			nameFormControl: new FormControl("", [Validators.required]),
			commentFormControl: new FormControl("", [Validators.required]),
		});

		this.form.controls["sumFormControl"].valueChanges
			.pipe(
				filter((value) => !!value),
				map((value) => value.toString().trim()),
				debounceTime(300),
				distinctUntilChanged()
			)
			.subscribe({
				next: () => {
					this.getCommissionSize();
				},
			});

		this.presetCard();
	}

	get cardNumberFormatError() {
		return this.form.get("cardNumberFormControl")?.hasError("minlength");
	}

	get cardNumberRequiredError() {
		return this.form.get("cardNumberFormControl")?.hasError("required");
	}

	get sumRequiredError() {
		return this.form.get("sumFormControl")?.hasError("required");
	}

	get nameRequiredError() {
		return this.form.get("nameFormControl")?.hasError("required");
	}

	get commentRequiredError() {
		return this.form.get("commentFormControl")?.hasError("required");
	}

	getVirtualPosId() {
		this.paymentToCardDataService.getVirtualPosP2pId().subscribe({
			next: (res) => {
				this.virtualPosId = res.id;
				this.currency = res.currency;
				this.initForm();
			},
			error: () => {
				this.loaded = true;
			},
			complete: () => {
				this.loaded = true;
			},
		});
	}

	loadCardList() {
		this.cardDataService.loadActiveCardList().subscribe({
			next: (res) => {
				this.savedCardList = res.content;
			},
		});
	}

	getCommissionSize() {
		const data: CommissionsSizeDto = {
			agentId: localStorage.getItem("agent_id") ?? "",
			virtualPosId: this.virtualPosId,
			sumList: [this.form.get("sumFormControl")?.value],
		};

		if (+this.form.get("sumFormControl")?.value !== 0) {
			this.paymentToCardDataService.getCommission(data).subscribe({
				next: (res) => {
					this.commission = res.resultAgentCommission.toString();
				},
			});
		} else {
			this.commission = "0";
		}
	}

	presetCard() {
		this.form.controls["sumFormControl"].patchValue(this.activatedRoute.snapshot.queryParamMap.get("sum") ?? "");
		this.form.controls["nameFormControl"].patchValue(this.activatedRoute.snapshot.queryParamMap.get("name") ?? "");
		this.form.controls["commentFormControl"].patchValue(this.activatedRoute.snapshot.queryParamMap.get("comment") ?? "");
		this.form.controls["currencyFormControl"].patchValue(this.activatedRoute.snapshot.queryParamMap.get("currency") ?? "KZT");

		this.token = this.activatedRoute.snapshot.queryParamMap.get("token") ?? "";

		if (this.activatedRoute.snapshot.queryParamMap.get("sum")) {
			this.tabSelect = "saved";
			this.p2pRefund = true;
			this.presetSum = true;

			this.form.controls["sumFormControl"].disable();
			this.form.controls["nameFormControl"].disable();
		}

		if (this.token) {
			this.paymentToCardDataService.getPaymentInfo(this.token).subscribe({
				next: (res) => {
					this.time = +((res.payment_expiration_date - Date.now()) / 1000).toFixed(0);
					this.timer = setInterval(() => {
						if (this.time >= 0) {
							this.time--;
						} else {
							clearInterval(this.timer);
						}
					}, 1000);
				},
			});
		}
	}

	selectTab(tabName: string) {
		if (this.tabSelect !== tabName) {
			this.tabSelect = tabName;
		}
		this.clearCardFrom();
		this.selectedCard = undefined;
	}

	get cardFromValidate(): boolean {
		return (
			!(
				this.cardNumberFrom.length === 16 &&
				this.mount.length === 2 &&
				this.year.length === 2 &&
				this.cvv.length === 3 &&
				!this.cardHolderNameService.validName(this.cardHolderNameFrom)
			) && !this.selectedCard
		);
	}

	selectCard(item: SavedCardDto) {
		this.selectedCard = item;
	}

	checkMaxMount() {
		if (this.mount.length === 1 && +this.mount > 1) {
			this.mount = `0${this.mount}`;
		}
		if (+this.mount > 12) {
			setTimeout(() => {
				this.mount = this.mount.substring(0, 1);
			}, 0);
		}
	}

	sendP2p() {
		this.isSending = true;
		let data: P2PRequestDto | undefined;

		if (!this.selectedCard) {
			data = {
				P2PRequest: {
					cvc: this.cvv,
					currency: this.form.get("currencyFormControl")?.value,
					infoline: this.form.get("commentFormControl")?.value,
					agent_id: localStorage.getItem("agent_id") ?? "",
					is_by_saved_card: false,
					payer_card: this.cardNumberFrom,
					exp_date: `${this.mount}${this.year}`,
					payer_card_holder: this.cardHolderNameFrom,
					payee_card: this.form.get("cardNumberFormControl")?.value,
					full_name: this.form.get("nameFormControl")?.value,
					transfer_sum: +this.form.get("sumFormControl")?.value,
				},
			};
		}

		if (this.selectedCard) {
			data = {
				P2PRequest: {
					currency: this.form.get("currencyFormControl")?.value,
					infoline: this.form.get("commentFormControl")?.value,
					agent_id: localStorage.getItem("agent_id") ?? "",
					is_by_saved_card: true,
					masked_card: this.selectedCard?.card_num,
					payer_bank_id: this.selectedCard.card_bank_id,
					exp_date: this.selectedCard?.validity,
					payee_card: this.form.get("cardNumberFormControl")?.value,
					full_name: this.form.get("nameFormControl")?.value,
					transfer_sum: +this.form.get("sumFormControl")?.value,
				},
			};
		}

		if (this.p2pRefund) {
			data ? (data.P2PRequest.refund_token = this.token ?? undefined) : "";
		}

		if (data) {
			this.paymentToCardDataService.sendP2p(data).subscribe({
				next: () => {
					this.notificationService.setConfiguration(
						"icon-3",
						"warning",
						"PAYMENT_TO_CARD_PAGE.NOTIFICATION.SUCCESS.TITLE",
						"PAYMENT_TO_CARD_PAGE.NOTIFICATION.SUCCESS.DESCRIPTION"
					);

					this.notificationService.showNotification();
					this.clearFormData();

					if (this.p2pRefund) {
						clearInterval(this.timer);
						this.router.navigate(["/payment-to-card"]);
						setTimeout(() => {
							this.notificationService.showNotification();
							this.time = 0;
						}, 0);
					}
				},
				error: () => {
					this.isSending = false;
				},
			});
		}
	}

	clearFormData() {
		this.selectedCard = undefined;
		this.clearCardFrom();

		if (this.activatedRoute.snapshot.queryParamMap.get("sum")) {
			this.form.controls["cardNumberFormControl"].reset();
			this.form.controls["currencyFormControl"].reset();
			this.form.controls["commentFormControl"].reset();
		} else {
			this.form.reset();
		}
		this.commission = "0";
		this.isSending = false;

		this.form.get("currencyFormControl")?.patchValue("KZT");
	}

	clearCardFrom() {
		this.cardNumberFrom = "";
		this.mount = "";
		this.year = "";
		this.cvv = "";
		this.cardHolderNameFrom = "";
	}

	clearForm() {
		this.selectedCard = undefined;
		if (!this.p2pRefund) {
			this.clearFormData();
		} else {
			this.clearCardFrom();
			if (this.activatedRoute.snapshot.queryParamMap.get("sum")) {
				this.form.controls["cardNumberFormControl"].reset();
				this.form.controls["currencyFormControl"].reset();
				this.form.controls["commentFormControl"].reset();
			} else {
				this.form.reset();
			}
		}
	}
}
